import type { EngagementMetrics, OverviewStats } from '../services/analyticsAPI';

export const EMAIL_ENGAGEMENT_LABEL = 'Email engagement';
export const EMAIL_ENGAGEMENT_HINT = 'Opens and replies per email sent';
export const SOCIAL_ENGAGEMENT_LABEL = 'Social engagement';
export const SOCIAL_ENGAGEMENT_HINT = 'Likes, comments and shares per view';
export const TREND_WINDOW_LABEL = 'Last 6 months';

export const EMPTY_ENGAGEMENT: EngagementMetrics = {
  emailsSent: 0,
  trueOpens: 0,
  coachReplies: 0,
  openRate: null,
  replyRate: null,
  emailEngagementRate: null,
  posts: { total: 0, x: 0, youtube: 0 },
  views: 0,
  likes: 0,
  comments: 0,
  shares: 0,
  avgViewsPerPost: null,
  socialEngagementRate: null,
  schoolsContacted: 0,
};

/** Shown for a rate with nothing to divide by (no emails sent, no views). */
export const NO_RATE = 'N/A';

/** A rate as "30.8%". Null or bad input reads as "N/A". */
export function formatRate(rate: number | string | null | undefined): string {
  const n = Number(rate);
  if (rate === null || rate === undefined || rate === '' || !Number.isFinite(n)) return NO_RATE;
  return `${n.toFixed(1)}%`;
}

/** "25.0% opened, 6.0% replied", or "No emails sent yet". */
export function emailRatesLine(e: EngagementMetrics): string {
  if (!e.emailsSent || (e.openRate == null && e.replyRate == null)) return 'No emails sent yet';
  return `${formatRate(e.openRate)} opened, ${formatRate(e.replyRate)} replied`;
}

/** The engagement object with every field filled, so cards never read undefined. */
export function withDefaults(e: Partial<EngagementMetrics> | null | undefined): EngagementMetrics {
  if (!e) return EMPTY_ENGAGEMENT;
  return {
    ...EMPTY_ENGAGEMENT,
    ...e,
    posts: { ...EMPTY_ENGAGEMENT.posts, ...(e.posts || {}) },
  };
}

/** Likes per post, rounded to a whole number. */
export function avgLikesPerPost(e: EngagementMetrics): number {
  return e.posts.total > 0 ? Math.round(e.likes / e.posts.total) : 0;
}

function toRate(numerator: number, denominator: number): number | null {
  if (!denominator) return null;
  return Math.round(Math.min(100, (numerator / denominator) * 100) * 10) / 10;
}

/**
 * The engagement object from an overview response. An overview without one
 * (an older backend) is read from its legacy fields.
 */
export function engagementFromOverview(overview: OverviewStats | null | undefined): EngagementMetrics {
  if (!overview) return EMPTY_ENGAGEMENT;
  if (overview.engagement) return withDefaults(overview.engagement);
  const t = overview.totalEngagementMetrics || ({} as OverviewStats['totalEngagementMetrics']);
  const a = overview.additionalMetrics || ({} as OverviewStats['additionalMetrics']);
  const views = Number(t.totalViews) || 0;
  const likes = Number(t.totalLikes) || 0;
  const comments = Number(t.totalComments) || 0;
  const shares = Number(t.totalShares) || 0;
  // The old engagementRate mixed email and social. Rebuild the email rate from counts.
  const emailsSent = Number(overview.emailsSent) || 0;
  const trueOpens = Number(a.emailsOpened) || 0;
  const coachReplies = Number(a.emailsReplied) || 0;
  return withDefaults({
    emailsSent,
    trueOpens,
    coachReplies,
    openRate: toRate(trueOpens, emailsSent),
    replyRate: toRate(coachReplies, emailsSent),
    emailEngagementRate: toRate(trueOpens + coachReplies, emailsSent),
    views,
    likes,
    comments,
    shares,
    socialEngagementRate: toRate(likes + comments + shares, views),
    schoolsContacted: Number(overview.totalOutreach) || 0,
  });
}
